/**
 * Created by Abner Sui on 10/09/2020.
 * Description:
 *
 * ------ maintenance history ------
 * Changed by Daniel on 3/30/2021 Changed the verification logic
 * Updated by Simon Zhao 10/11/2021 Added logic for dynamic assignee
 * 10/13/2021 Simon Zhao Added a max-length limitation for the staged name input.
 * 12/24/2021 Marcus Zhao Added a multi entity dropdown control component.
 */

import { Component, OnInit, OnDestroy, Input, ViewChild, AfterViewInit } from '@angular/core';
import { createSelector, select, Store } from '@ngrx/store';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { AppState } from '../../../redux';
import { ApprovalTaskConfiguration, AssigneeDefinition, RelationshipAssignee, Stage, TaskSubType, TaskType } from '../../../tamalelibs/models/workflow.model';
import { WorkflowActionTypes } from '../../../tamalelibs/redux/actions/workflow.actions';
import { currentWorkflowStageSelector } from '../../../tamalelibs/redux/reducers/workflow.reducer';
import { WorkflowService } from '../../../tamalelibs/services/workflow.service';
import { TaskDefinitionConfig, TaskDefinitionEventTypes, TaskDefinitionNeedValidControl } from './task-definition.model';
import * as _lodash from 'lodash';
import { UntypedFormGroup } from '@angular/forms';
import { StringLiteralsPipe } from '../../../pipes/translate.pipe';
import { validRelationshipTypeSelector } from '../../../redux/reducers/valid-relationship-type.reducer';
import { EntityType } from './../../../tamalelibs/models/entity-type.model';
import { ValidRelationshipType } from './../../../tamalelibs/models/valid-relationship-type.model';
import { currentWorkflowFieldsSelector } from './../../../tamalelibs/redux/reducers/workflow.reducer';
import { SingleSelectDropdownConfig, SingleSelectDropdownFilterConfig } from './../../../widgets/single-select-dropdown-filter/single-select-dropdown-filter.model';
import { UntypedFormControl, Validators } from '@angular/forms';
import { businessConstants } from '../../../constants/business.constants';
import { ArrayHelperService } from '../../../tamalelibs/services/array-helper.service';
import { WorkflowTriggerSettings } from '../../../tamalelibs/models/workflow-config.model';
import { MultiSelDropdownConfig, MultiSelDropdownFilterConfig } from '../../../widgets/multi-sel-dropdown-filter/multi-sel-dropdown-filter.model';
import { ALL_VALUE } from '../../../tamalelibs/constants/business.constants';
import { defaultSource } from '../../../tamalelibs/models/tile-column.model';

export const CREATE_STAGE = ' (Create Stage)';
export const StaticAssigneeDefault = 'Select users or teams';

@Component({
    selector: 'task-definition',
    templateUrl: './task-definition.component.html',
    styleUrls: ['./task-definition.component.scss']
})
export class TaskDefinitionComponent implements OnInit, OnDestroy, AfterViewInit {

    accessList: string;

    assigneeOptionErrorMessage: string = StringLiteralsPipe.translate('general.required_error_message');

    dropdownConfig: MultiSelDropdownFilterConfig = new MultiSelDropdownFilterConfig(true);

    isErrorOccured: boolean;

    @Input() config: TaskDefinitionConfig;

    @ViewChild('dualList', { static: false }) dualListInst;

    /**
     * gets the placeholder for entity link
     *
     * @readonly
     * @type {string}
     * @memberof TaskDefinitionComponent
     */
    get entityLinkLabel(): string {
        return this._entityLinkLabel ? this._entityLinkLabel : StringLiteralsPipe.translate('workflow.select_a_custom_field');
    }

    /**
     * the data source for the custom field dropdown list.
     *
     * @type {SingleSelectDropdownFilterConfig}
     * @memberof TaskDefinitionComponent
     */
    frmEntityControlConfig: SingleSelectDropdownFilterConfig = new SingleSelectDropdownFilterConfig(true);
    /**
     * the candidates fixed at the top of the custom filed dropdown list.
     *
     * @memberof TaskDefinitionComponent
     */
    freezingEntityCtrlCfg = [SingleSelectDropdownConfig.parse(businessConstants.previousTaskOwner.id, false, StringLiteralsPipe.translate(businessConstants.previousTaskOwner.name))];

    /**
    * a flag indicating whether the dynamic assignee assignment is on
    *
    * @readonly
    * @type {boolean}
    * @memberof TaskDefinitionComponent
    */

    @ViewChild('kendoc', { static: false }) kendoc;

    @ViewChild('kendom', { static: false }) kendom;

    listItems: Array<{ id: string, name: string, type: string, shortName: string, aliases: string[] }> = [];

    loading = true;

    @Input() needValidControl: TaskDefinitionNeedValidControl;

    nameDuplicated = false;

    /**
     * the placeholder for the relationship link
     *
     * @readonly
     * @memberof TaskDefinitionComponent
     */
    get relationshipTypeLinkLabel() {
        return this._relationshipTypeLinkLabel ? this._relationshipTypeLinkLabel : StringLiteralsPipe.translate('workflow.select_a_relationship');
    }
    /**
     * the data source for the relationship dropdown list.
     *
     * @type {SingleSelectDropdownFilterConfig}
     * @memberof TaskDefinitionComponent
     */
    relationshipTypeConfig: SingleSelectDropdownFilterConfig = new SingleSelectDropdownFilterConfig(true);
    selectedValues: Array<{ id: string, name: string, type: string, shortName: string, aliases: string[] }> = [];
    stageData: Array<Stage>;
    stageLoading = true;
    sourceName = StaticAssigneeDefault;

    formErrorMsg = {
        triggerMethodRequired: true,
    };
    noAssigneeTypeChecked = false; // true if at least one assignee type has been checked.
    assigneeType = {
        isStaticAssigneeChecked: false,
        isDynamicAssigneeChecked: false
    };

    sourceInvalid = false;
    staticDefaultValueDataSource: BehaviorSubject<Array<MultiSelDropdownConfig>> = new BehaviorSubject<Array<MultiSelDropdownConfig>>([]);
    staticDefaultValueLoading = false;

    private _destroySubscriptions: Array<Subscription> = [];
    private _entityLinkLabel: string;
    private _filterStageSubject$: Subject<string> = new Subject();
    private _filterSubject$: Subject<string> = new Subject();
    private _relationshipTypeLinkLabel: string;
    private _stageData: Array<Stage>;
    private _suffix = CREATE_STAGE;
    private _spliceCount = 0; // caculate delteCount for set stage Color.

    private get _selectedFrmEntityControlId() {
        return this.config.taskDefinition.assignee && this.config.taskDefinition.assignee.dependField
            && this.config.taskDefinition.assignee.dependField.fieldDefId ? this.config.taskDefinition.assignee.dependField.fieldDefId : undefined;
    }

    private get _selectedRelationshipTypeId() {
        return this.config.taskDefinition.assignee && this.config.taskDefinition.assignee.dependField
            && this.config.taskDefinition.assignee.dependField.relationshipType ? this.config.taskDefinition.assignee.dependField.relationshipType : undefined;
    }

    constructor(
        private _store: Store<AppState>,
        private _workflowService: WorkflowService,
    ) { }

    ngAfterViewInit(): void {
        // Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
        // In order to avoiding the exception caused by long staged name, TPM determined to set a maximum of 64 for the staged name.
        // And kendo combobox provides no maxLength attribute and supporter provides this solution.
        if (this.kendoc && this.kendoc.searchbar && this.kendoc.searchbar.input && this.kendoc.searchbar.input.nativeElement) {
            this.kendoc.searchbar.input.nativeElement.setAttribute('maxlength', 64);
        }

    }

    ngOnInit(): void {
        this._initListData();
        this._initStageData();
        this._initDynamicAndStaticAssignee();
        if (this.config.taskDefinition.type === TaskType.APPROVAL) {
            this.config.taskDefinition.approvalTask = new ApprovalTaskConfiguration();
            // TODO below code should bind to UI, which is configurable by UI
            this.config.taskDefinition.approvalTask.type = TaskSubType.SINGLE;
        }

        this.config.actionSubject$.subscribe(res => {
            if (res.actiontype === TaskDefinitionEventTypes.VERIFY) {
                this._validateAssigneeErrorMsgStatus();
            }
        }
        );
    }

    ngOnDestroy(): void {
        this.needValidControl.name = true;
        this.needValidControl.assignee = true;
        this.needValidControl.stage = true;
    }

    deleteStageItem(event, item: Stage): void {
        if (this.config.taskDefinition.stage && this.config.taskDefinition.stage.id === item.id) {
            this.config.taskDefinition.stage = null;
        }
        this._deleteStage(item);
    }

    filterAssignee(event): void {
        this.loading = true;
        this._filterSubject$.next(event);
    }

    filterStage(event): void {
        this.stageLoading = true;
        this._filterStageSubject$.next(event);
    }

    focusAssignee(): void {
        this.kendom.toggle(true);
    }

    focusStage(): void {
        this.kendoc.toggle(true);
    }

    onChangeName() {
        this.needValidControl.name = false;
        this.config.configChanged = true;
    }

    onChangeDescription() {
        this.config.configChanged = true;
    }

    onChangeStage(event): void {
        this.config.configChanged = true;
        this.needValidControl.stage = false;
        // create on the fly
        if (this.config.taskDefinition.stage && !this.config.taskDefinition.stage.id) {
            const stageName = this.config.taskDefinition.stage.name.substr(0, this.config.taskDefinition.stage.name.length - this._suffix.length);
            const stage = Stage.parse(stageName);
            stage.color = Stage.obtainUnusedColor(this._stageData);
            this.stageData = [...this._stageData];
            this.stageData.unshift(stage);
            this.config.taskDefinition.stage = stage;
            this.config.taskDefinition.stageColor = stage.color;
            this._store.dispatch({
                type: WorkflowActionTypes.ADD_STAGE,
                payload: stage,
            });
        } else if (this.config.taskDefinition.stage && this.config.taskDefinition.stage.id) {
            this.config.taskDefinition.stageColor = this.config.taskDefinition.stage.color;
        }
    }

    onDynamicAssigneeCheckedChange() {
        if (!this.assigneeType.isDynamicAssigneeChecked) {
            this.config.taskDefinition.assignee.dependField = new RelationshipAssignee(null, null);
            this._relationshipTypeLinkLabel = null;
            this._entityLinkLabel = null;
        }
        this._validateAssigneeErrorMsgStatus();
    }

    onStaticAssigneeCheckedChange() {
        if (!this.assigneeType.isStaticAssigneeChecked) {
            this.config.taskDefinition.assignee.teams = null;
            this.config.taskDefinition.assignee.users = null;

            this.sourceName = StaticAssigneeDefault;
            this._initListData();
        }
        this._validateAssigneeErrorMsgStatus();
    }

    onClickSource(event) {
        this.dropdownConfig.open$.next(event.target);
    }

    onFrmEntityControlItemClick(e: any) {
        this.frmEntityControlConfig.open$.next(e.target);
    }

    onRelationshipTypeItemClick(e: any) {
        this.relationshipTypeConfig.open$.next(e.target);
    }

    private _validateAssigneeErrorMsgStatus() {
        const required_error = StringLiteralsPipe.translate('general.required_error_message');
        const at_least_one_error = StringLiteralsPipe.translate('general.select_at_least_one_checkbox_error_message');

        if (this.assigneeType.isDynamicAssigneeChecked) {
            if (this.entityLinkLabel === StringLiteralsPipe.translate('workflow.select_a_custom_field')) {
                this.assigneeOptionErrorMessage = required_error;
                this.isErrorOccured = true;
            } else if (this.assigneeType.isStaticAssigneeChecked) {
                if (this.sourceName === StaticAssigneeDefault) {
                    this.assigneeOptionErrorMessage = required_error;
                    this.isErrorOccured = true;
                } else {
                    this.isErrorOccured = false;
                }
            } else {
                if (!this.config.taskDefinition.assignee.teams) {
                    this.config.taskDefinition.assignee.teams = null;
                }
                if (!this.config.taskDefinition.assignee.users) {
                    this.config.taskDefinition.assignee.users = null;
                }
                this.isErrorOccured = false;
            }
        } else if (this.assigneeType.isStaticAssigneeChecked) {
            if (this.sourceName === StaticAssigneeDefault) {
                this.assigneeOptionErrorMessage = required_error;
                this.isErrorOccured = true;
            } else {
                this.isErrorOccured = false;
            }
        } else {
            this.assigneeOptionErrorMessage = at_least_one_error;
            this.isErrorOccured = true;
        }
    }

    private _createdynamicAssigneeFormGroup(): UntypedFormGroup {
        const frmG = new UntypedFormGroup({});
        const fieldCtrl = new UntypedFormControl(this.config.taskDefinition.assignee.dependField[RelationshipAssignee.FieldDefIdPropName], Validators.required);
        frmG.addControl(RelationshipAssignee.FieldDefIdPropName, fieldCtrl);
        this._destroySubscriptions.push(fieldCtrl.valueChanges.subscribe(r => this.config.taskDefinition.assignee.dependField.fieldDefId = r));
        // remove the required validation from the relationship type field because there's a case in which only assignee field is required.
        const relationshipCtrl = new UntypedFormControl(this.config.taskDefinition.assignee.dependField[RelationshipAssignee.RelationshipTypePropName]);
        frmG.addControl(RelationshipAssignee.RelationshipTypePropName, relationshipCtrl);
        this._destroySubscriptions.push(relationshipCtrl.valueChanges.subscribe(r => this.config.taskDefinition.assignee.dependField.relationshipType = r));

        return frmG;
    }

    /**
     * make sure all dynamic assignee related fields have been initialized.
     *
     * @private
     * @memberof TaskDefinitionComponent
     */
    private _checkDynamicAssignee() {
        if (!this.config.taskDefinition.assignee) {
            this.config.taskDefinition.assignee = new AssigneeDefinition();
        }

        if (!this.config.taskDefinition.assignee.dependField) {
            this.config.taskDefinition.assignee.dependField = new RelationshipAssignee(null, null);
        }
    }

    private _deleteStage(item: Stage): void {
        let index = this._stageData.findIndex(ss => ss.id === item.id);
        if (index > -1) {
            this._stageData.splice(index, 1);
        }
        index = this.stageData.findIndex(ss => ss.id === item.id);
        if (index > -1) {
            this.stageData.splice(index, 1);
        }
        this._spliceCount++;
        this._workflowService.workflowActionSubject$.next({
            type: WorkflowActionTypes.DELETE_STAGE,
            payload: {
                id: item.id,
            },
        });
    }

    private _initListData(): void {
        this.dropdownConfig.data = [];
        this.accessList = this.config.taskDefinition.assignee.teams || '';
        if (this.config.taskDefinition.assignee.users) {
            this.accessList += ',' + this.config.taskDefinition.assignee.users;
        }
        const accessListSelector = createSelector(
            (state: AppState) => state.users,
            (state: AppState) => state.teams,
            (allUser, allTeam) => {
                if (allUser.length === 0 && allTeam.length === 0) {
                    return [];
                }
                const users = allUser.map(item => {
                    return {
                        type: 'user',
                        ...item
                    };
                });
                const teams = allTeam.map(item => {
                    return {
                        type: 'team',
                        ...item
                    };
                });
                return users.concat(teams);
            }
        );
        this._destroySubscriptions.push(
            this._store.pipe(
                select(accessListSelector),
                filter((list: any) => list.length > 0),
            ).subscribe(list => {
                ArrayHelperService.sort(list, 'name');
                list.forEach(item => {
                    let checked = false;
                    if (this.config.taskDefinition.assignee?.teams?.includes(item.id) || this.config.taskDefinition.assignee?.users?.includes(item.id)) {
                        checked = true;
                    }
                    this.dropdownConfig.data.push(MultiSelDropdownConfig.parse(item.id, checked, item.name, item.type, false));
                });
                const sources: Array<any> = this.dropdownConfig.data.filter(item => item.checked);
                if (sources?.length > 0) {
                    this.sourceName = sources.map(item => item.name).join(', ');
                    if (this.sourceName !== StaticAssigneeDefault) {
                        this.assigneeType.isStaticAssigneeChecked = true;
                    }
                }
                this.loading = false;
            }),
            this.dropdownConfig.onChangeValue$.subscribe(data => this._valueChangeSource(data)),
        );

        if (this.config.isImport && this.selectedValues.length === 0) {
            this.needValidControl.assignee = true;
        }
    }

    private _initStageData(): void {
        this._destroySubscriptions.push(
            this._store.pipe(
                select(currentWorkflowStageSelector),
            ).subscribe(item => {
                this._stageData = item;
                this.stageData = [...this._stageData];
                this.stageLoading = false;
            }),

            this._filterStageSubject$.pipe(
                debounceTime(250),
            ).subscribe(str => {
                if (str) {
                    const lower = str.toLowerCase();
                    this.stageData = [];
                    let exactMatch = false;
                    this._stageData.forEach(item => {
                        if (item.name.toLowerCase() === lower) {
                            exactMatch = true;
                            this.stageData.push(item);
                        } else if (item.name.toLowerCase().indexOf(str.toLowerCase()) > -1) {
                            this.stageData.push(item);
                        }
                    });
                    if (!exactMatch) {
                        const mockStage = {
                            id: '',
                            name: `${str}${this._suffix}`,
                            color: 0
                        };
                        this.stageData.push(mockStage);
                    }
                } else {
                    this.stageData = [...this._stageData];
                }
                this.stageLoading = false;
            }),
        );
    }

    private _initDynamicAndStaticAssignee() {
        // dynamic assignee
        this.relationshipTypeConfig.sortCfg = { propName: 'name', isAscOrder: true };
        this.assigneeType.isDynamicAssigneeChecked = this.config.taskDefinition && !!this.config.taskDefinition.assignee && !!this.config.taskDefinition.assignee.dependField && !!this.config.taskDefinition.assignee.dependField.fieldDefId;

        this._checkDynamicAssignee();
        this.needValidControl.dynamicAssigneeFormGroup = this._createdynamicAssigneeFormGroup();
        this._initFrmEntityControl();
        this._initRelationType();
        // static assignee
        this.assigneeType.isStaticAssigneeChecked = this.config.taskDefinition.assignee.teams?.length > 0 || this.config.taskDefinition.assignee.users?.length > 0;
    }

    private _initFrmEntityControl(): void {
        this._destroySubscriptions.push(
            this._store.pipe(
                select(currentWorkflowFieldsSelector),
            ).subscribe(fieldBriefs => {
                if (fieldBriefs && fieldBriefs.length) {
                    fieldBriefs.forEach(item => {
                        const index = this.frmEntityControlConfig.data.findIndex(res => res.id === item.id);
                        if (index === -1) {
                            const newFieldItem = {
                                id: item.fieldId,
                                name: item.controlName,
                                selected: this._selectedFrmEntityControlId === item.fieldId
                            };
                            this.frmEntityControlConfig.data.push(newFieldItem);
                            if (newFieldItem.selected) {
                                this._entityLinkLabel = newFieldItem.name;
                            }
                        }
                    });
                }
                const selectedFreezingItem = this.freezingEntityCtrlCfg.find(item => item.id === this._selectedFrmEntityControlId);
                if (selectedFreezingItem) {
                    this._entityLinkLabel = selectedFreezingItem.name;
                }
            })
        );

        // register value change event
        this._destroySubscriptions.push(
            this.frmEntityControlConfig.onChangeValue$.subscribe(data => {
                if (data) {
                    this.config.configChanged = true;
                    this._entityLinkLabel = data.name;
                    if (this.needValidControl.dynamicAssigneeFormGroup) {
                        this.needValidControl.dynamicAssigneeFormGroup.get(RelationshipAssignee.FieldDefIdPropName).setValue(data.id);
                    }
                    if (data.name !== StringLiteralsPipe.translate('workflow.select_a_custom_field')) {
                        this.assigneeType.isDynamicAssigneeChecked = true;
                    }
                    this._validateAssigneeErrorMsgStatus();
                }
            }),
        );
    }

    private _initRelationType(): void {
        this._destroySubscriptions.push(this._store.pipe(
            select(validRelationshipTypeSelector)
        ).subscribe(data => {
            const allValidRelationshipTypes: Array<ValidRelationshipType> = data;
            if (allValidRelationshipTypes && allValidRelationshipTypes.length > 0) {
                allValidRelationshipTypes.forEach(rt => {
                    const entityTypes = [EntityType.CONTACT.id, EntityType.TEAM.id, EntityType.CONTACTSFORVALIDRELTYPE.id];
                    if (entityTypes.includes(rt.childEntityType.id) && entityTypes.includes(rt.parentEntityType.id)) {
                        const newRelItem = {
                            id: rt.relationshipType.id,
                            name: rt.relationshipType.name,
                            selected: this._selectedRelationshipTypeId === rt.relationshipType.id
                        };

                        this.relationshipTypeConfig.data.push(newRelItem);
                        if (newRelItem.selected) {
                            this._relationshipTypeLinkLabel = newRelItem.name;
                        }
                    }
                });
            }
        }));

        // register value change event
        this._destroySubscriptions.push(
            this.relationshipTypeConfig.onChangeValue$.subscribe(data => {
                if (data) {
                    this.config.configChanged = true;
                    this._relationshipTypeLinkLabel = data.name;
                    if (this.needValidControl.dynamicAssigneeFormGroup) {
                        this.needValidControl.dynamicAssigneeFormGroup.get(RelationshipAssignee.RelationshipTypePropName).setValue(data.id);
                    }
                    this._validateAssigneeErrorMsgStatus();
                }
            }),
        );
    }

    private _changeTriggerMethodErrorMsgStatus() {
        if (this.assigneeType.isStaticAssigneeChecked || this.assigneeType.isDynamicAssigneeChecked) {
            this.formErrorMsg.triggerMethodRequired = false;
        } else {
            this.formErrorMsg.triggerMethodRequired = true;
        }
    }

    private _valueChangeSource(event): void {
        const value = event === ALL_VALUE ? [{ id: event, name: 'All' }] : event;

        this.needValidControl.assignee = false;
        let teams: string;
        let users: string;
        value.forEach(item => {
            if (item.type === 'team') {
                teams = teams ? (teams + ',' + item.id) : item.id;
            } else {
                users = users ? (users + ',' + item.id) : item.id;
            }
        });
        if (teams) {
            this.config.taskDefinition.assignee.teams = teams;
        } else {
            delete this.config.taskDefinition.assignee.teams;
        }
        if (users) {
            this.config.taskDefinition.assignee.users = users;
        } else {
            delete this.config.taskDefinition.assignee.users;
        }
        this.config.configChanged = true;
        if (value?.length > 0) {
            this.sourceName = value.map(item => item.name).join(', ');
            this.assigneeType.isStaticAssigneeChecked = true;
        } else {
            this.sourceName = StaticAssigneeDefault;
        }

        this._validateAssigneeErrorMsgStatus();
    }
}
